/**
 * Edge rules for ER diagrams.
 * Maps cardinality symbols to draw.io ER arrow style extras,
 * assigns connection points, and separates parallel edges.
 */

import type { IRGraph, IREdge } from '../types';

const CARD_ARROWS: Record<string, string> = {
  '||': 'ERone',
  'o|': 'ERzeroToOne',
  '|{': 'ERoneToMany',
  '}|': 'ERoneToMany',
  'o{': 'ERzeroToMany',
  '}o': 'ERzeroToMany',
  'ONLY_ONE': 'ERone',
  'ZERO_OR_ONE': 'ERzeroToOne',
  'ONE_OR_MORE': 'ERoneToMany',
  'ZERO_OR_MORE': 'ERzeroToMany',
};

export function applyEdgeRules(graph: IRGraph): void {
  applyCardinalityArrows(graph);
  applyErConnectionPoints(graph);
  applyErParallelSeparation(graph);
}

function applyCardinalityArrows(graph: IRGraph): void {
  for (const edge of graph.edges) {
    const cardinality = (edge.meta?.cardinality as { from: string; to: string }) ?? {
      from: '||',
      to: 'o{',
    };
    const startArrow = CARD_ARROWS[cardinality.from] ?? 'ERone';
    const endArrow = CARD_ARROWS[cardinality.to] ?? 'ERzeroToMany';
    if (!edge.meta) edge.meta = {};
    edge.meta.edgeStyleExtras =
      `startArrow=${startArrow};startFill=0;endArrow=${endArrow};endFill=0;`;
  }
}

/**
 * Assign exit/entry connection points based on relative node positions.
 * LR layout: horizontal comparison → East/West sides.
 * TB layout: vertical comparison → South/North sides.
 */
function applyErConnectionPoints(graph: IRGraph): void {
  const isHorizontal = graph.direction === 'LR' || graph.direction === 'RL';

  for (const edge of graph.edges) {
    const src = graph.nodes.get(edge.source);
    const tgt = graph.nodes.get(edge.target);
    if (!src || !tgt || src.x === undefined || tgt.x === undefined) continue;
    if (!edge.meta) edge.meta = {};

    if (isHorizontal) {
      const srcCenterX = src.x + src.width / 2;
      const tgtCenterX = tgt.x + tgt.width / 2;
      if (srcCenterX <= tgtCenterX) {
        // source left of target → exit East, enter West
        edge.meta.exitPoint = { x: 1, y: 0.5 };
        edge.meta.entryPoint = { x: 0, y: 0.5 };
        edge.meta.exitSide = 'east';
        edge.meta.entrySide = 'west';
      } else {
        // source right of target → exit West, enter East
        edge.meta.exitPoint = { x: 0, y: 0.5 };
        edge.meta.entryPoint = { x: 1, y: 0.5 };
        edge.meta.exitSide = 'west';
        edge.meta.entrySide = 'east';
      }
    } else {
      const srcCenterY = (src.y ?? 0) + src.height / 2;
      const tgtCenterY = (tgt.y ?? 0) + tgt.height / 2;
      if (srcCenterY <= tgtCenterY) {
        // source above target → exit South, enter North
        edge.meta.exitPoint = { x: 0.5, y: 1 };
        edge.meta.entryPoint = { x: 0.5, y: 0 };
        edge.meta.exitSide = 'south';
        edge.meta.entrySide = 'north';
      } else {
        // source below target → exit North, enter South
        edge.meta.exitPoint = { x: 0.5, y: 0 };
        edge.meta.entryPoint = { x: 0.5, y: 1 };
        edge.meta.exitSide = 'north';
        edge.meta.entrySide = 'south';
      }
    }
  }
}

/**
 * When multiple edges share the same entity+side, stagger their
 * connection coordinates so they don't overlap.
 */
function applyErParallelSeparation(graph: IRGraph): void {
  // Group edges by (entityId, side) for both source and target ends
  const exitGroups = new Map<string, IREdge[]>();
  const entryGroups = new Map<string, IREdge[]>();

  for (const edge of graph.edges) {
    const exitSide = edge.meta?.exitSide as string | undefined;
    const entrySide = edge.meta?.entrySide as string | undefined;
    if (exitSide) {
      const key = `${edge.source}:${exitSide}`;
      if (!exitGroups.has(key)) exitGroups.set(key, []);
      exitGroups.get(key)!.push(edge);
    }
    if (entrySide) {
      const key = `${edge.target}:${entrySide}`;
      if (!entryGroups.has(key)) entryGroups.set(key, []);
      entryGroups.get(key)!.push(edge);
    }
  }

  // Stagger exit points
  for (const [, edges] of exitGroups) {
    if (edges.length < 2) continue;
    distributePoints(edges, 'exitPoint', edges[0].meta?.exitSide as string);
  }

  // Stagger entry points
  for (const [, edges] of entryGroups) {
    if (edges.length < 2) continue;
    distributePoints(edges, 'entryPoint', edges[0].meta?.entrySide as string);
  }
}

function distributePoints(
  edges: IREdge[],
  pointKey: 'exitPoint' | 'entryPoint',
  side: string,
): void {
  const count = edges.length;
  const min = 0.2;
  const max = 0.8;
  const step = count > 1 ? (max - min) / (count - 1) : 0;

  for (let i = 0; i < count; i++) {
    const pt = edges[i].meta?.[pointKey] as { x: number; y: number } | undefined;
    if (!pt) continue;
    const offset = min + step * i;

    if (side === 'east' || side === 'west') {
      // Stagger along Y axis
      pt.y = offset;
    } else {
      // North/South — stagger along X axis
      pt.x = offset;
    }
  }
}
